import type { ReactNode } from "react";
import { FONT, T, PETROL } from "../theme";
import type { FrameData, GalleryData, WSection, Teardown } from "../types";

export function SiteFrameNode({ data }: { data: FrameData }) {
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      {/* browser-chrome header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          height: 44,
          padding: "0 16px",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          border: `1px solid ${T.line}`,
          borderBottom: "none",
          background: T.white,
        }}
      >
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ width: 11, height: 11, borderRadius: "50%", background: "#F87171", display: "inline-block" }} />
          <i style={{ width: 11, height: 11, borderRadius: "50%", background: "#FBBF24", display: "inline-block" }} />
          <i style={{ width: 11, height: 11, borderRadius: "50%", background: "#34D399", display: "inline-block" }} />
        </span>
        <span
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 13,
            color: T.ink2,
            background: T.bg,
            borderRadius: 8,
            padding: "5px 10px",
            marginRight: 40,
          }}
        >
          {data.label}
        </span>
      </div>
      {/* full-page screenshot */}
      <div
        style={{
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          overflow: "hidden",
          border: `1px solid ${T.line}`,
          background: "#faf7f2",
          boxShadow: "0 20px 50px rgba(0,0,0,0.16)",
          fontSize: 0,
          lineHeight: 0,
        }}
      >
        <img src={data.img} alt={data.label} draggable={false} decoding="async" style={{ width: data.w, height: "auto", display: "block" }} />
      </div>
    </div>
  );
}

export function HeroConceptNode({ data }: { data: { label: string; img: string; w: number } }) {
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 40, padding: "0 16px", borderTopLeftRadius: 12, borderTopRightRadius: 12, border: `1px solid ${T.line}`, borderBottom: "none", background: T.white }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{data.label}</span>
      </div>
      <div style={{ position: "relative", borderBottomLeftRadius: 12, borderBottomRightRadius: 12, overflow: "hidden", border: `1px solid ${T.line}`, boxShadow: "0 20px 50px rgba(0,0,0,0.16)", background: "#faf7f2" }}>
        <img src={data.img} alt={data.label} draggable={false} decoding="async" style={{ width: data.w, height: "auto", display: "block" }} />
        {/* our real hero copy, centered low like the live hero */}
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", textAlign: "center", paddingTop: "11%", paddingInline: 40 }}>
          <div style={{ fontSize: 44, lineHeight: 1.0, letterSpacing: "-0.055em", color: "#1b1a18", fontWeight: 400 }}>AI business solutions</div>
          <div style={{ marginTop: 14, maxWidth: 380, fontSize: 15, lineHeight: 1.4, color: "#54504b" }}>We learn how your business works, then design and run the AI that solves your hardest problems.</div>
          <div style={{ marginTop: 20, background: "#1b1a18", color: "#fff", borderRadius: 999, padding: "10px 22px", fontSize: 14, fontWeight: 500 }}>Schedule a demo</div>
        </div>
      </div>
    </div>
  );
}

export function StockCandidateNode({ data }: { data: { label: string; img: string; w: number } }) {
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 40, padding: "0 16px", borderTopLeftRadius: 12, borderTopRightRadius: 12, border: `1px solid ${T.line}`, borderBottom: "none", background: T.white }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{data.label}</span>
      </div>
      <div style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12, overflow: "hidden", border: `1px solid ${T.line}`, boxShadow: "0 20px 50px rgba(0,0,0,0.16)", background: "#faf7f2", fontSize: 0, lineHeight: 0 }}>
        <img src={data.img} alt={data.label} draggable={false} decoding="async" style={{ width: data.w, height: "auto", display: "block" }} />
      </div>
    </div>
  );
}

export function GalleryBoxNode({ data }: { data: GalleryData }) {
  return (
    <div style={{ fontFamily: FONT, width: data.boxW, background: T.white, border: `1px solid ${T.line}`, borderRadius: 20, boxShadow: "0 24px 60px rgba(0,0,0,0.10)", overflow: "hidden" }}>
      <div style={{ padding: "26px 30px", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.02em", color: T.ink }}>{data.title}</span>
          {data.tag && <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0" }}>{data.tag}</span>}
        </div>
        {data.sub && <div style={{ marginTop: 8, fontSize: 20, color: T.ink2 }}>{data.sub}</div>}
      </div>
      <div style={{ padding: 24, display: "grid", gridTemplateColumns: `repeat(${data.cols}, 1fr)`, gap: 16 }}>
        {data.items.map((it, i) => (
          <div key={i} style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${T.line}`, background: "#faf7f2" }}>
            <img src={it.img} alt={it.label || ""} draggable={false} decoding="async" style={{ width: "100%", height: data.cellH, objectFit: "cover", display: "block" }} />
            {it.label && <div style={{ padding: "8px 11px", fontSize: 13, lineHeight: 1.3, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export const WFRAME = "#F6F2EC";
export function UiTag({ t }: { t: string }) {
  return <span style={{ position: "absolute", top: 8, left: 12, fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#B9AEA1" }}>{t}</span>;
}

export function WireframeSection({ s }: { s: WSection }) {
  const eyebrow = s.eyebrow ? <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: PETROL, marginBottom: 8 }}>{s.eyebrow}</div> : null;
  const H = s.headline ? <div style={{ fontSize: 25, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.12 }}>{s.headline}</div> : null;
  const Sub = s.sub ? <div style={{ marginTop: 9, fontSize: 14, color: T.ink2, lineHeight: 1.5, maxWidth: 580, marginInline: "auto" }}>{s.sub}</div> : null;
  const CTA = s.cta ? <span style={{ marginTop: 16, display: "inline-block", background: T.ink, color: "#fff", fontSize: 13, fontWeight: 600, borderRadius: 999, padding: "9px 18px" }}>{s.cta}</span> : null;
  const box: React.CSSProperties = { position: "relative", background: WFRAME, border: `1px solid ${T.line}`, borderRadius: 12, padding: "34px 26px 26px" };
  let body: ReactNode;
  switch (s.layout) {
    case "hero":
      body = (<div style={{ ...box, textAlign: "center", overflow: "hidden" }}><UiTag t="Hero · image background" /><div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,#DCE7F1,#F6F2EC)" }} /><div style={{ position: "relative", paddingTop: 6 }}>{eyebrow}{H}{Sub}<div>{CTA}</div></div></div>); break;
    case "logos":
      body = (<div style={{ ...box, textAlign: "center" }}><UiTag t="Logo strip" /><div style={{ fontSize: 13, fontWeight: 600, color: T.ink2, marginBottom: 16 }}>{s.headline}</div><div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12 }}>{(s.logos || []).map((l, i) => <span key={i} style={{ fontSize: 13, fontWeight: 700, color: "#8A8178", background: T.white, border: `1px solid ${T.line}`, borderRadius: 7, padding: "7px 12px" }}>{l}</span>)}</div></div>); break;
    case "problem":
      body = (<div style={box}><UiTag t="Centered statement + chips" /><div style={{ textAlign: "center" }}>{H}{Sub}</div><div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10 }}>{(s.chips || []).map((c, i) => <span key={i} style={{ fontSize: 12.5, color: "#B4552F", background: "rgba(180,85,47,0.10)", borderRadius: 999, padding: "7px 14px", fontWeight: 600 }}>{c}</span>)}</div></div>); break;
    case "duo": case "proof":
      body = (<div style={box}><UiTag t={s.layout === "duo" ? "2 feature cards" : "2 proof cards"} /><div style={{ textAlign: "center", marginBottom: 20 }}>{H}{Sub}</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>{(s.cards || []).map((c, i) => <div key={i} style={{ background: T.white, border: `1px solid ${T.line}`, borderRadius: 10, padding: 18 }}><div style={{ fontSize: 15, fontWeight: 800, color: T.ink, marginBottom: 6 }}>{c.t}</div><div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>{c.b}</div></div>)}</div></div>); break;
    case "steps":
      body = (<div style={box}><UiTag t="3 steps" /><div style={{ textAlign: "center", marginBottom: 20 }}>{H}{Sub}</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>{(s.steps || []).map((c, i) => <div key={i} style={{ background: T.white, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16 }}><div style={{ fontSize: 12, fontWeight: 800, color: PETROL, marginBottom: 6 }}>{i + 1}</div><div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 4 }}>{c.t}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.45 }}>{c.b}</div></div>)}</div></div>); break;
    case "grid4":
      body = (<div style={box}><UiTag t="4 outcome cards" /><div style={{ textAlign: "center", marginBottom: 20 }}>{H}{Sub}</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>{(s.cards || []).map((c, i) => <div key={i} style={{ background: T.white, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16 }}><div style={{ fontSize: 14.5, fontWeight: 800, color: T.ink, marginBottom: 5 }}>{c.t}</div><div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.45 }}>{c.b}</div></div>)}</div></div>); break;
    case "cta":
      body = (<div style={{ ...box, textAlign: "center", background: "#1C1917", border: "none" }}><UiTag t="Closing CTA band" /><div style={{ fontSize: 25, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.12 }}>{s.headline}</div>{s.sub && <div style={{ marginTop: 9, fontSize: 14, color: "rgba(255,255,255,0.72)", lineHeight: 1.5, maxWidth: 520, marginInline: "auto" }}>{s.sub}</div>}{s.cta && <span style={{ marginTop: 16, display: "inline-block", background: "#fff", color: "#1C1917", fontSize: 13, fontWeight: 700, borderRadius: 999, padding: "9px 20px" }}>{s.cta}</span>}</div>); break;
    case "footer":
      body = (<div style={{ ...box, background: "#F0EBE3" }}><UiTag t="Footer" /><div style={{ fontSize: 13, color: T.ink2 }}>{s.sub}</div></div>); break;
    case "statement":
      body = (<div style={{ ...box, textAlign: "center", background: "linear-gradient(180deg,#FBF9F5,#F3EFE8)" }}><UiTag t="Inspirational statement" /><div style={{ position: "relative", paddingTop: 4 }}>{eyebrow}<div style={{ fontSize: 27, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.15, maxWidth: 560, marginInline: "auto" }}>{s.headline}</div>{s.sub && <div style={{ marginTop: 13, fontSize: 14.5, color: T.ink2, lineHeight: 1.6, maxWidth: 520, marginInline: "auto" }}>{s.sub}</div>}{s.chips && <div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>{s.chips.map((c, i) => <span key={i} style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: "0.02em", color: PETROL, padding: "0 14px", borderRight: i < (s.chips!.length - 1) ? `1px solid ${T.line}` : "none" }}>{c}</span>)}</div>}</div></div>); break;
    default:
      body = <div style={box}>{H}{Sub}</div>;
  }
  return (
    <div style={{ padding: "22px 26px", borderBottom: `1px dashed ${T.line}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.white, background: PETROL, borderRadius: 6, padding: "3px 8px" }}>{s.n}</span>
        <span style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>{s.name}</span>
        <span style={{ fontSize: 12.5, color: "#8A8178", fontStyle: "italic", lineHeight: 1.4, flex: 1, minWidth: 220 }}>{s.goal}</span>
      </div>
      {body}
    </div>
  );
}
export function WireframeNode({ data }: { data: { w?: number; sections: WSection[]; label?: string } }) {
  return (
    <div style={{ fontFamily: FONT, width: data.w || 900, background: T.white, border: data.label ? `2px solid ${PETROL}` : `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 30px 70px rgba(0,0,0,0.13)", overflow: "hidden" }}>
      {data.label && <div style={{ background: PETROL, color: "#fff", fontSize: 13, fontWeight: 800, letterSpacing: "0.04em", textAlign: "center", padding: "8px 0" }}>{data.label}</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 46, padding: "0 16px", borderBottom: `1px solid ${T.line}`, background: T.bg }}>
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ width: 11, height: 11, borderRadius: "50%", background: "#F87171", display: "inline-block" }} />
          <i style={{ width: 11, height: 11, borderRadius: "50%", background: "#FBBF24", display: "inline-block" }} />
          <i style={{ width: 11, height: 11, borderRadius: "50%", background: "#34D399", display: "inline-block" }} />
        </span>
        <span style={{ flex: 1, textAlign: "center", fontSize: 13, color: T.ink2, background: T.white, borderRadius: 8, padding: "5px 10px", marginRight: 40 }}>tailormind.co — homepage</span>
      </div>
      {data.sections.map((s) => <WireframeSection key={s.n} s={s} />)}
    </div>
  );
}

export function TeardownNode({ data }: { data: Teardown }) {
  const rows: [string, string, string][] = [];
  return (
    <div style={{ fontFamily: FONT, width: 660, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.10)", overflow: "hidden" }}>
      <div style={{ padding: "22px 26px 18px", borderBottom: `1px solid ${T.line}`, background: T.bg }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{data.site}</span>
          <span style={{ fontSize: 11.5, color: "#9A9188", fontFamily: "ui-monospace, monospace" }}>{data.url}</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 13.5, color: T.ink2, lineHeight: 1.45 }}>{data.resemblance}</div>
        <div style={{ marginTop: 10, display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: PETROL, background: "rgba(43,85,96,0.10)", borderRadius: 999, padding: "4px 11px" }}>{data.vibe}</div>
      </div>
      <div style={{ padding: "6px 26px 8px" }}>
        {data.sections.map((s, i) => (
          <div key={i} style={{ padding: "16px 0", borderBottom: i < data.sections.length - 1 ? `1px dashed ${T.line}` : "none" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 9, marginBottom: 8 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: T.white, background: T.ink, borderRadius: 5, padding: "2px 7px" }}>{s.n}</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>{s.name}</span>
            </div>
            {([["Goal", s.goal, "#B8842B"], ["Content", s.content, "#2B5560"], ["How it works", s.fulfills, "#5E7D5E"]] as [string, string, string][]).map(([lbl, txt, c], k) => (
              <div key={k} style={{ marginBottom: 6, display: "flex", gap: 10 }}>
                <span style={{ flexShrink: 0, width: 86, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: c, paddingTop: 2 }}>{lbl}</span>
                <span style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{txt}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ padding: "16px 26px 22px", background: "#F0EBE3" }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: PETROL, marginBottom: 8 }}>Steal for us</div>
        <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
          {data.steal.map((s, i) => <li key={i} style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.45 }}>{s}</li>)}
        </ul>
      </div>
    </div>
  );
}

